<template>
  <div class="recruit-container">
    <!-- 左侧文案区 -->
    <div class="left-panel" :class="{ 'english-spacing': isEnglish }">
      <h2 class="title">{{ t("pages.recruit.leftPanel.title") }}</h2>
      <p class="paragraph">
        {{ t("pages.recruit.leftPanel.paragraph1") }}
      </p>
      <p class="paragraph">
        {{ t("pages.recruit.leftPanel.paragraph2") }}
      </p>
      <p class="paragraph">
        {{ t("pages.recruit.leftPanel.paragraph3") }}
      </p>
      <p class="paragraph">
        {{ t("pages.recruit.leftPanel.paragraph4") }}
      </p>
    </div>

    <!-- 右侧岗位区 - 卡片式手风琴 -->
    <div class="right-panel">
      <el-collapse v-model="activeNames" accordion>
        <el-collapse-item
          v-for="(job, index) in jobPositions"
          :key="index"
          :name="index.toString()"
        >
          <template #title>
            <span class="job-title">
              <i :class="job.icon" class="job-icon"></i>
              <!-- 岗位图标 -->
              {{ t(job.titleKey) }}
            </span>
          </template>
          <div class="job-details">
            <div
              class="text-block"
              v-html="renderContent(t(job.detailsKey))"
            ></div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { useLangStore } from '../../pinia/stores/lang';

export default {
  name: 'RecruitView',
  setup() {
    const { t } = useI18n();
    const langStore = useLangStore();
    
    // 判断当前是否为英文
    const isEnglish = computed(() => {
      return !langStore.currentLang.startsWith('zh');
    });

    // 控制手风琴展开项
    const activeNames = ref("0"); // 默认展开第一项，可以设为 null 不展开
    const renderContent = (content) => {
      if (!content) return "";
      return content
        .split("---")
        .map((paragraph) => `<p>${paragraph}</p>`)
        .join("");
    };

    // 岗位列表数据
    const allJobs = [
      {
        titleKey: "pages.recruit.jobs.rdEngineer.title",
        detailsKey: "pages.recruit.jobs.rdEngineer.details",
        icon: "el-icon-lightbulb",
      },
      {
        titleKey: "pages.recruit.jobs.testEngineer.title",
        detailsKey: "pages.recruit.jobs.testEngineer.details",
        icon: "el-icon-lightbulb",
      },
      {
        titleKey: "pages.recruit.jobs.opticalEngineer.title",
        detailsKey: "pages.recruit.jobs.opticalEngineer.details",
        icon: "el-icon-lightbulb",
      },
      {
        titleKey: "pages.recruit.jobs.salesSpecialist.title",
        detailsKey: "pages.recruit.jobs.salesSpecialist.details",
        icon: "el-icon-lightbulb",
        lang: 'zh' // 只在中文显示
      },
      // 更多岗位...
    ];

    const jobPositions = computed(() => {
      const currentLang = langStore.currentLang;
      return allJobs.filter(job => {
        // 如果没有设置语言，或者当前语言匹配，则显示
        return !job.lang || currentLang.startsWith(job.lang);
      });
    });

    return {
      t,
      activeNames,
      renderContent,
      jobPositions,
      isEnglish
    };
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/content-blocks.scss";

.recruit-container {
  display: flex;
  gap: 50px; // 左右区域间距
  padding: 100px 40px 40px; // 顶级页面需要更多顶部padding
  max-width: 1400px;
  margin: 0 auto;
}

.left-panel {
  flex: 1; // 占据可用空间
  max-width: 450px; // 可以限制最大宽度

  .title {
    font-size: 2.5rem; // 放大标题字号
    font-weight: bold;
    color: #003366; // 企业主色调 (深蓝)
    margin-bottom: 30px;
  }

  .paragraph {
    font-size: 1rem;
    color: #555; // 深灰色正文
    line-height: 1.8; // 增加行间距
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // 英文版本增加更大的段间距
  &.english-spacing .paragraph {
    margin-bottom: 60px;
    line-height: 2.0;
  }
}

.right-panel {
  flex: 1.5; // 右侧区域稍宽
  max-width: 700px; // 限制最大宽度
  max-height: 700px;
  overflow: auto;
  .el-collapse {
    border: none; // 移除默认边框
    background-color: transparent; // 背景透明
  }

  .el-collapse-item {
    margin-bottom: 20px; // 卡片间距
    background-color: rgba(255, 255, 255, 0.6) !important; // 卡片背景白色
    border-radius: 8px; // 圆角边框
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); // 底部阴影
    overflow: hidden; // 配合圆角

    // 覆盖 Element Plus 默认样式
    :deep(.el-collapse-item__header) {
      padding: 20px 25px;
      font-size: 1.1rem;
      font-weight: 600;
      color: #333; // 深灰色标题
      border-bottom: 1px solid #eef0f3; // 头部底部分隔线
      transition: background-color 0.3s ease;
      background-color: transparent !important; // 确保header透明

      &:hover {
        background-color: transparent !important; // 确保hover时也透明
      }

      .job-title {
        display: flex;
        align-items: center;
      }

      .job-icon {
        margin-right: 10px;
        font-size: 1.3em; // 图标大小
        color: #0056b3; // 图标颜色 (蓝色)
      }

      // 展开箭头样式
      .el-collapse-item__arrow {
        font-size: 1.2em;
        color: #0056b3; // 箭头颜色 (蓝色)
        transition: transform 0.3s ease;
      }
      &.is-active .el-collapse-item__arrow {
        transform: rotate(90deg);
      }
    }

    :deep(.el-collapse-item__wrap) {
      border-bottom: none; // 移除内容区域的底部边框
      background-color: transparent !important; // 使用 transparent
    }

    :deep(.el-collapse-item__content) {
      padding: 25px;
      font-size: 0.95rem;
      color: #666; // 浅灰色详情文字
      line-height: 1.7;

      p {
        margin-bottom: 10px;
        &:last-child {
          margin-bottom: 0;
        }
      }
      // 可以添加对列表等的样式
      ul,
      ol {
        padding-left: 20px;
        margin-bottom: 10px;
      }
    }
  }
}

// 响应式调整
@media screen and (max-width: 992px) {
  .recruit-container {
    flex-direction: column;
    gap: 30px;
    padding: 80px 20px 20px;
    max-width: 100%;
    box-sizing: border-box;
  }
  .left-panel,
  .right-panel {
    max-width: 100%;
  }
}

@media screen and (max-width: 768px) {
  .recruit-container {
    padding: 80px 15px 15px;
    gap: 20px;
    height: calc(100vh - 120px);
    overflow-y: auto;
    box-sizing: border-box;
  }
  
  .left-panel {
    .title {
      font-size: 2rem;
      margin-bottom: 20px;
    }
    
    .paragraph {
      font-size: 0.9rem;
      margin-bottom: 15px;
    }
  }
  
  .right-panel {
    max-height: none;
    overflow: visible;
    
    :deep(.el-collapse-item__header) {
      padding: 15px 20px;
      font-size: 1rem;
    }
    
    :deep(.el-collapse-item__content) {
      padding: 20px;
      font-size: 0.9rem;
    }
  }
}
</style> 